import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

export interface HistoryEntry {
  id: string;
  at: string | null;
  ended_at: string | null;
  kind: string;
  verb: string;
  /** proof | spec | toc | upload | delete | other — drives the icon and colour. */
  group: string;
  /** Which face of the project this belongs to; "" = the hub. */
  tab: string;
  subject: string;
  detail: string;
  status: string;
  error: string | null;
  project_id: string | null;
  project_name: string;
  live: boolean;
  progress: number | null;
}

export interface HistoryPage {
  entries: HistoryEntry[];
  next_before: string | null;
  projects: { id: string; name: string }[];
}

/**
 * The account's history. Polled while anything is live so a running job's
 * progress moves, and left alone otherwise — this is mostly a record of the
 * past, and the past does not change.
 */
export function useHistory(projectId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["history", projectId],
    queryFn: () => api<HistoryPage>(`/api/history${projectId ? `?project_id=${projectId}` : ""}`),
    enabled,
    refetchInterval: (q) =>
      (q.state.data?.entries ?? []).some((e) => e.live) ? 3000 : false,
  });
}

/**
 * How much work is running right now, for the nav badge — and the invalidation
 * that used to live in the activity tray.
 *
 * That tray was the only thing telling the shell a background job had ended,
 * which is the one change with no local mutation to hang a refresh off. Deleting
 * it without moving this would have left the sidebar counts stale after every
 * proof, in a way nobody would connect to the tray going away.
 */
export function useActiveWork() {
  const qc = useQueryClient();
  const running = useRef<Set<string>>(new Set());
  const { data } = useQuery({
    queryKey: ["active-jobs"],
    queryFn: () => api<{ jobs: { id: string }[] }>("/api/jobs/active").then((r) => r.jobs),
    refetchInterval: 3000,
  });

  const ids = (data ?? []).map((j) => j.id).join(",");
  useEffect(() => {
    const now = new Set((data ?? []).map((j) => j.id));
    const ended = [...running.current].some((id) => !now.has(id));
    running.current = now;
    if (ended) qc.invalidateQueries({ queryKey: ["projects"] });
    // `ids`, not `data`: the query hands back a fresh array every poll, which
    // would fire this on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, qc]);

  return { jobCount: (data ?? []).length };
}
